/**
 * SignupPrompt — soft account-creation modal shown after 2 games or 2
 * widgets. Grabs email + optional name and pipes them straight into the
 * newsletter subscribe endpoint (which is the only account we keep).
 */
import { useEffect, useState } from "react";
import { X, Mail, Newspaper } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ENGAGEMENT_EVENT,
  shouldPrompt,
  markSignedUp,
  dismissPrompt,
  getEngagement,
} from "@/lib/engagement";

export default function SignupPrompt() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [newsletter, setNewsletter] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Show the modal if the threshold was already met before mount
    // (e.g. counter incremented on a previous visit).
    if (shouldPrompt()) {
      // Wait a beat so the modal doesn't feel jarring during initial paint.
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
    const onBump = () => {
      if (shouldPrompt()) setOpen(true);
    };
    window.addEventListener(ENGAGEMENT_EVENT, onBump);
    return () => window.removeEventListener(ENGAGEMENT_EVENT, onBump);
  }, []);

  const close = () => {
    dismissPrompt();
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email: email.trim(), name: name.trim() || null });
      markSignedUp();
      toast.success("You're in — check your inbox for a welcome from Titan Wire.");
      setOpen(false);
    } catch (err) {
      // 409 = already subscribed — treat as success so we stop nagging them.
      if (err?.response?.status === 409) {
        markSignedUp();
        toast.success("Already on the list — welcome back.");
        setOpen(false);
      } else {
        toast.error(err?.response?.data?.detail || "Signup failed — try again");
      }
    } finally {
      setBusy(false);
    }
  };

  const eng = getEngagement();

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 anim-fade"
      data-testid="signup-prompt-overlay"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden anim-pop"
        onClick={(e) => e.stopPropagation()}
        data-testid="signup-prompt-modal"
      >
            {/* Header — solid titans-blue band, no gradient */}
            <div className="relative bg-[#0C2340] px-6 py-5 text-white">
              <button
                onClick={close}
                aria-label="Dismiss"
                className="absolute top-3 right-3 p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="signup-prompt-close"
              >
                <X size={16} />
              </button>
              <div className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.28em] text-[10px] text-titans-blue">
                <span className="inline-block h-[3px] w-6 bg-titans-red" />
                Two-Tone Blue Nation
              </div>
              <h2 className="mt-2 font-display uppercase text-2xl md:text-3xl leading-tight">
                You're one of us.<br />Keep the wire going.
              </h2>
              <p className="mt-2 text-sm text-white/80 leading-snug">
                You've played {eng.games ? `${eng.games} game${eng.games === 1 ? "" : "s"}` : ""}
                {eng.games && eng.widgets ? " and " : ""}
                {eng.widgets ? `used ${eng.widgets} widget${eng.widgets === 1 ? "" : "s"}` : ""}.
                Drop your email — you'll get the weekly Titan Wire recap and the notes we don't publish anywhere else.
              </p>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4" data-testid="signup-prompt-form">
              <div>
                <label className="block text-[10px] font-cond uppercase tracking-[0.2em] text-[#0C2340] mb-1">
                  Email <span className="text-titans-red">*</span>
                </label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@titansfan.com"
                  className="w-full border border-[#DBE2EA] focus:border-titans-blue focus:outline-none px-4 py-3 font-body text-[#0B1B2E] rounded-md"
                  data-testid="signup-prompt-email"
                />
              </div>
              <div>
                <label className="block text-[10px] font-cond uppercase tracking-[0.2em] text-[#0C2340] mb-1">
                  Name <span className="text-[#94A3B8]">(optional)</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Cam Ward"
                  className="w-full border border-[#DBE2EA] focus:border-titans-blue focus:outline-none px-4 py-3 font-body text-[#0B1B2E] rounded-md"
                  data-testid="signup-prompt-name"
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newsletter}
                  onChange={(e) => setNewsletter(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-[#DBE2EA] accent-[#0C2340]"
                  data-testid="signup-prompt-newsletter"
                />
                <span className="text-[13px] text-[#4B5563] leading-snug">
                  <span className="inline-flex items-center gap-1 font-semibold text-[#0B1B2E]">
                    <Newspaper size={13} /> Send me Titan Wire
                  </span>
                  <br />
                  Weekly recap, injury reports, and sharp analysis. No spam. Unsubscribe anytime.
                </span>
              </label>

              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={close}
                  className="text-[12px] font-cond uppercase tracking-[0.2em] text-[#64748B] hover:text-[#0C2340] transition-colors"
                  data-testid="signup-prompt-skip"
                >
                  Not now
                </button>
                <button
                  type="submit"
                  disabled={busy || !email}
                  className="btn-hero"
                  data-testid="signup-prompt-submit"
                >
                  <Mail size={14} className="text-white" />
                  {busy ? "Signing you in…" : "Count me in"}
                </button>
              </div>
              <p className="text-[10px] text-[#94A3B8] font-cond uppercase tracking-wider text-center pt-1">
                Free · Zero spam · Cancel from any email
              </p>
            </form>
      </div>
    </div>
  );
}
